import { useWhiteLabel } from "@/contexts/WhiteLabelContext";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import type { Invoice } from "@/hooks/useInvoices";

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

interface Props { invoice: Invoice }

export default function InvoicePDFView({ invoice }: Props) {
  const { whiteLabelSettings } = useWhiteLabel();
  const { settings } = useCompanySettings();
  const companyName = settings.company_name || whiteLabelSettings.companyName || "Sentinel Forge Systems";
  const logoUrl = settings.logo_url;

  return (
    <div id="invoice-pdf-content" className="bg-white text-gray-900 p-8 max-w-4xl mx-auto" style={{ fontFamily: "Arial, sans-serif" }}>
      <div data-pdf-block className="flex justify-between items-start mb-8 pb-6 border-b-2 border-gray-800">
        <div>
          <h1 className="text-3xl font-bold mb-1">INVOICE</h1>
          <p className="text-sm text-gray-600">#{invoice.invoice_number}</p>
        </div>
        <div className="text-right">
          {logoUrl && <img src={logoUrl} alt={companyName} className="h-12 ml-auto mb-2 object-contain" />}
          <p className="text-xl font-bold">{companyName}</p>
          {settings.company_address && <p className="text-xs text-gray-600 whitespace-pre-line">{settings.company_address}</p>}
          {settings.company_phone && <p className="text-xs text-gray-600">{settings.company_phone}</p>}
          {settings.company_email && <p className="text-xs text-gray-600">{settings.company_email}</p>}
        </div>
      </div>

      <div data-pdf-block className="grid grid-cols-2 gap-6 mb-8">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Bill To</p>
          <p className="font-bold">{invoice.client_name}</p>
          {invoice.client_company && <p>{invoice.client_company}</p>}
          {invoice.client_address && <p className="text-sm">{invoice.client_address}</p>}
          {invoice.client_email && <p className="text-sm">{invoice.client_email}</p>}
        </div>
        <div className="text-right space-y-1 text-sm">
          <div><span className="text-gray-500">Issue Date: </span><span className="font-semibold">{invoice.issue_date}</span></div>
          {invoice.due_date && <div><span className="text-gray-500">Due Date: </span><span className="font-semibold">{invoice.due_date}</span></div>}
          {invoice.payment_terms && <div><span className="text-gray-500">Terms: </span><span className="font-semibold">{invoice.payment_terms}</span></div>}
          <div><span className="text-gray-500">Status: </span><span className="font-semibold uppercase">{invoice.status}</span></div>
        </div>
      </div>

      <table data-pdf-block className="w-full mb-8 border-collapse">
        <thead>
          <tr className="bg-gray-800 text-white">
            <th className="p-3 text-left">Description</th>
            <th className="p-3 text-right w-20">Qty</th>
            <th className="p-3 text-right w-32">Unit Price</th>
            <th className="p-3 text-right w-32">Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.line_items.map((it, i) => (
            <tr key={i} className="border-b border-gray-200">
              <td className="p-3">{it.description}</td>
              <td className="p-3 text-right">{it.quantity}</td>
              <td className="p-3 text-right">{fmt(it.unit_price)}</td>
              <td className="p-3 text-right">{fmt(it.quantity * it.unit_price)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div data-pdf-block className="flex justify-end mb-8">
        <div className="w-72 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span>{fmt(invoice.subtotal)}</span></div>
          {invoice.discount > 0 && <div className="flex justify-between"><span className="text-gray-600">Discount</span><span>-{fmt(invoice.discount)}</span></div>}
          {invoice.tax_amount > 0 && <div className="flex justify-between"><span className="text-gray-600">Tax ({invoice.tax_rate}%)</span><span>{fmt(invoice.tax_amount)}</span></div>}
          <div className="flex justify-between text-xl font-bold pt-3 border-t-2 border-gray-800"><span>Total Due</span><span>{fmt(invoice.total - invoice.amount_paid)}</span></div>
          {invoice.amount_paid > 0 && <div className="flex justify-between text-green-700"><span>Paid</span><span>-{fmt(invoice.amount_paid)}</span></div>}
        </div>
      </div>

      {invoice.notes && (
        <div data-pdf-block className="mb-6 p-4 bg-gray-50 border-l-4 border-gray-800">
          <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Notes</p>
          <p className="text-sm whitespace-pre-wrap">{invoice.notes}</p>
        </div>
      )}

      <div data-pdf-block className="text-center text-xs text-gray-500 pt-6 border-t">
        Thank you for your business.
      </div>
    </div>
  );
}
